/**
 * Client-side plan-vs-actual matcher.
 *
 * This is a port of the server's `matchNewPlanSessions()` from
 * `server/services/metricsEngine.js`. It exists client-side because the
 * Node backend runs stale code until restarted (Node caches require()'d
 * modules), so `/api/metrics` returns a matchedSessions list computed
 * against an OLD plan that no longer matches what the user sees in the
 * Train tab. Running the matcher in the React bundle decouples it from
 * the server restart cycle, mirroring how `planGenerator` runs here too.
 *
 * Keep this file in sync with the server matcher when matching semantics
 * evolve. A shared package would be nicer long-term; for now the two
 * files must move together.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function parseLocalDate(s) {
  if (!s) return new Date(NaN);
  if (typeof s === 'string' && !s.includes('T')) return new Date(s + 'T00:00:00');
  return new Date(s);
}

const RUN_TYPES = new Set(['Run', 'TrailRun', 'Treadmill', 'VirtualRun']);
const HIKE_TYPES = new Set(['Hike', 'Walk']);

/**
 * Match planned sessions against Strava activities. Returns an array of
 * row objects: { date, planned, actual, status, plannedKm, actualKm, ... }
 *
 * Status codes (string, lowercase — same as server):
 *   'matched'  — actual completed ≥ 70% of prescribed km/duration
 *   'partial'  — actual found but under 70% target
 *   'today'    — planned for today, no actual yet
 *   'upcoming' — planned for a future date
 *   'missed'   — past date, no matching actual
 *   'extra'    — actual activity with no matching planned session
 */
function matchPlanSessions(plannedSessions, activities) {
  if (!plannedSessions?.length) return [];

  const usedActivityIds = new Set();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // ±1 day window: candidate activities whose date falls within
  // [plannedDate-1, plannedDate+1] days (inclusive) and whose type matches.
  // windowMs = 0 restricts to the planned day itself (two-pass resolution).
  function candidatesForDate(dateStr, allowedTypes, windowMs = DAY_MS) {
    const planned = parseLocalDate(dateStr);
    planned.setHours(0, 0, 0, 0);
    const dayBefore = +planned - windowMs;
    const dayAfter = +planned + windowMs;

    return (activities || []).filter((a) => {
      if (usedActivityIds.has(a.id)) return false;
      const actType = a.type || '';
      if (!allowedTypes.has(actType)) return false;
      const aDate = new Date(a.start_date || a.date);
      aDate.setHours(0, 0, 0, 0);
      const t = +aDate;
      return t >= dayBefore && t <= dayAfter;
    });
  }

  // Find the best activity for one session within the given window.
  function findMatch(session, windowMs) {
    let match = null;
    let matchType = 'distance';

    if (session.type === 'hike') {
      matchType = 'hike';
      const candidates = candidatesForDate(session.date, HIKE_TYPES, windowMs);
      if (candidates.length > 0) {
        match = candidates.sort((a, b) => (b.moving_time_s || 0) - (a.moving_time_s || 0))[0];
      }
    } else if (session.type === 'time-on-feet') {
      matchType = 'duration';
      const candidates = candidatesForDate(session.date, RUN_TYPES, windowMs);
      if (session.plannedDuration && candidates.length > 0) {
        const targetSec = session.plannedDuration * 60;
        const durationMatches = candidates.filter((a) => {
          const dur = a.moving_time_s || 0;
          return dur >= targetSec * 0.8 && dur <= targetSec * 1.2;
        });
        match = durationMatches.length
          ? durationMatches.sort((a, b) => Math.abs((a.moving_time_s || 0) - targetSec) - Math.abs((b.moving_time_s || 0) - targetSec))[0]
          : candidates.sort((a, b) => (b.moving_time_s || 0) - (a.moving_time_s || 0))[0];
      }
    } else if (session.type === 'vertical') {
      matchType = 'elevation';
      const candidates = candidatesForDate(session.date, RUN_TYPES, windowMs);
      if (session.plannedElevation && candidates.length > 0) {
        const elevMatches = candidates.filter((a) => (a.elev_m || 0) >= session.plannedElevation * 0.85);
        match = elevMatches.length
          ? elevMatches.sort((a, b) => Math.abs((a.elev_m || 0) - session.plannedElevation) - Math.abs((b.elev_m || 0) - session.plannedElevation))[0]
          : null;
      }
    } else {
      const candidates = candidatesForDate(session.date, RUN_TYPES, windowMs);
      if (session.plannedKm && candidates.length > 0) {
        match = candidates.sort((a, b) =>
          Math.abs((a.distance_m || 0) / 1000 - session.plannedKm) -
          Math.abs((b.distance_m || 0) / 1000 - session.plannedKm)
        )[0];
      }
    }

    return { match, matchType };
  }

  // Resolve matches in TWO passes: same-day first, then the ±1-day window.
  //
  // v9 fix (B2B pair ambiguity, §Plan-matching window): a single greedy
  // ±1-day pass let a SKIPPED B2B day-1 steal day-2's long run — adjacent
  // sessions have overlapping windows and day-1 is processed first.
  // Same-day attachments now always win before any spillover into
  // adjacent days is considered. Mirrors the server matcher exactly.
  const resolvedMatches = new Map(); // session (by reference) -> { match, matchType }
  for (const windowMs of [0, DAY_MS]) {
    for (const session of plannedSessions) {
      if (resolvedMatches.has(session)) continue;
      const r = findMatch(session, windowMs);
      if (r.match) {
        usedActivityIds.add(r.match.id);
        resolvedMatches.set(session, r);
      }
    }
  }

  const rows = [];
  for (const session of plannedSessions) {
    const plannedDate = parseLocalDate(session.date);
    plannedDate.setHours(0, 0, 0, 0);
    const isFuture = +plannedDate > +today;
    const isToday = +plannedDate === +today;

    const resolved = resolvedMatches.get(session) || { match: null, matchType: 'distance' };
    const match = resolved.match;
    const matchType = resolved.matchType;

    let status;
    if (match) {
      let completed = false;
      if (matchType === 'duration' && session.plannedDuration) {
        completed = (match.moving_time_s || 0) >= session.plannedDuration * 60 * 0.7;
      } else if (matchType === 'elevation' && session.plannedElevation) {
        completed = (match.elev_m || 0) >= session.plannedElevation * 0.7;
      } else if (matchType === 'hike') {
        completed = true;
      } else if (session.plannedKm) {
        completed = (match.distance_m || 0) / 1000 >= session.plannedKm * 0.7;
      } else {
        completed = true;
      }
      status = completed ? 'matched' : 'partial';
    } else if (isToday) {
      status = 'today';
    } else if (isFuture) {
      status = 'upcoming';
    } else {
      status = 'missed';
    }

    const actualKm = match ? (match.distance_m || 0) / 1000 : null;
    const actualDuration = match ? (match.moving_time_s || 0) / 60 : null;
    const actualElevation = match ? (match.elev_m || 0) : null;

    rows.push({
      date: session.date,
      planned: session,
      actual: match || null,
      status,
      plannedKm: session.plannedKm ?? null,
      actualKm: actualKm != null ? +actualKm.toFixed(2) : null,
      plannedDuration: session.plannedDuration || null,
      actualDuration: actualDuration != null ? +actualDuration.toFixed(0) : null,
      plannedElevation: session.plannedElevation || null,
      actualElevation: actualElevation != null ? Math.round(actualElevation) : null,
    });
  }

  // Extras: unmatched PAST activities appear as their own rows. Future
  // activities (shouldn't exist from Strava, but defensive) are skipped.
  for (const a of activities || []) {
    if (usedActivityIds.has(a.id)) continue;
    const aDate = new Date(a.start_date || a.date);
    aDate.setHours(0, 0, 0, 0);
    if (+aDate > +today) continue;
    // Local-date string from the activity's local date (not .toISOString()
    // which would UTC-shift in positive offsets).
    const y = aDate.getFullYear();
    const m = String(aDate.getMonth() + 1).padStart(2, '0');
    const d = String(aDate.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    rows.push({
      date: dateStr,
      planned: null,
      actual: a,
      status: 'extra',
      plannedKm: null,
      actualKm: (a.distance_m || 0) / 1000,
      plannedDuration: null,
      actualDuration: (a.moving_time_s || 0) / 60,
      plannedElevation: null,
      actualElevation: a.elev_m || 0,
    });
  }

  return rows.sort((a, b) => a.date.localeCompare(b.date));
}

module.exports = { matchPlanSessions, RUN_TYPES, HIKE_TYPES };
